import { type StartTrainingAttendancePlan } from '../models/StartTrainingAttendancePlan'
import { useEffect, useState } from 'react'
import {
  type UpdateStartTrainingAttendancePlanFormValues
} from '../pages/pageComponents/planning/priorityActivities/PriorityOne/StartTrainingAttendancePlanModal'
import { GetStartTrainingAttendancePlans, LockActivity, UnlockActivity } from '../services/AnnualPlanService'

interface UseStartTrainingAttendancePlansType {
  startTrainingAttendancePlans: StartTrainingAttendancePlan[]
  isLoading: boolean
  reloadTrainingAttendancePlans: () => Promise<void>
}

export const useStartTrainingAttendancePlans = (priorityId: number, implementationPhase: boolean): UseStartTrainingAttendancePlansType => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [startTrainingAttendancePlans, setStartTrainingAttendancePlans] = useState<StartTrainingAttendancePlan[]>([])

  const loadTrainingAttendancePlans = async (): Promise<void> => {
    setIsLoading(true)

    const trainingAttendancePlans = await GetStartTrainingAttendancePlans(priorityId, implementationPhase)
    setStartTrainingAttendancePlans(trainingAttendancePlans)

    setIsLoading(false)
  }

  useEffect(() => {
    void loadTrainingAttendancePlans()
  }, [])

  return { startTrainingAttendancePlans, isLoading, reloadTrainingAttendancePlans: loadTrainingAttendancePlans }
}

interface UseStartTrainingAttendancePlanModalType {
  handleAddTrainingAttendancePlan: () => void
  handleEditTrainingAttendancePlan: (startTrainingAttendancePlan: StartTrainingAttendancePlan) => Promise<void>
  handleCloseModal: () => Promise<void>
  showTrainingAttendancePlanModal: boolean
  initialValues?: UpdateStartTrainingAttendancePlanFormValues
}

export const useStartTrainingAttendancePlanModal = (implementationPhase: boolean): UseStartTrainingAttendancePlanModalType => {
  const [initialValues, setInitialValues] = useState<UpdateStartTrainingAttendancePlanFormValues>()
  const [showTrainingAttendancePlanModal, setShowTrainingAttendancePlanModal] = useState(false)

  const handleAddTrainingAttendancePlan = (): void => {
    setInitialValues(undefined)
    setShowTrainingAttendancePlanModal(true)
  }

  const handleEditTrainingAttendancePlan = async (startTrainingAttendancePlan: StartTrainingAttendancePlan): Promise<void> => {
    setInitialValues({
      ...startTrainingAttendancePlan,
      startTrainingId: startTrainingAttendancePlan.startTraining.id ?? null,
      budgetRequest: startTrainingAttendancePlan.budgetRequest.replace('$', ''),
      isImplementationPhase: implementationPhase
    })
    setShowTrainingAttendancePlanModal(true)
    await LockActivity(startTrainingAttendancePlan.id, 'START Training Attendance Plan')
  }

  const handleCloseModal = async (): Promise<void> => {
    setShowTrainingAttendancePlanModal(false)
    if (initialValues != null) {
      await UnlockActivity(initialValues?.id ?? 0, 'START Training Attendance Plan')
    }
  }

  return {
    handleAddTrainingAttendancePlan,
    handleEditTrainingAttendancePlan,
    handleCloseModal,
    showTrainingAttendancePlanModal,
    initialValues
  }
}
